import type { JsonObject, KnowledgeCredentialField } from '../api/types';
import type { FieldIssue } from '../forms/schema';

/**
 * kinds の settingsSchema から接続フォームの欄を作る純関数（S6 E16）。
 *
 * ここは JSON Schema の実装ではなく、描ける型（string・integer・number・boolean・string の配列）だけを拾う
 * 限定サブセット。最終判定は常にサーバー（422 invalid_settings / invalid_credential）で、ここを通っても受理を意味しない。
 * 未対応の型（object・入れ子の array）は黙って飛ばし、その項目はサーバーの既定に任せる。
 */

export type SettingsFieldKind = 'text' | 'integer' | 'number' | 'boolean' | 'lines';

export interface SettingsField {
  key: string;
  label: string;
  hint: string | null;
  kind: SettingsFieldKind;
  required: boolean;
  defaultValue: unknown;
}

/** 欄が保持する生の入力値。checkbox は boolean、ほかは文字列（lines は 1 行 1 件）。 */
export type FormDraft = Record<string, string | boolean>;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function fieldKind(property: Record<string, unknown>): SettingsFieldKind | null {
  const type = property['type'];
  if (type === 'string') return 'text';
  if (type === 'integer') return 'integer';
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'array' && asRecord(property['items'])?.['type'] === 'string') return 'lines';
  return null;
}

export function settingsFields(schema: unknown): SettingsField[] {
  const root = asRecord(schema);
  const properties = asRecord(root?.['properties']) ?? {};
  const requiredRaw = root?.['required'];
  const required = Array.isArray(requiredRaw) ? (requiredRaw as unknown[]) : [];
  const fields: SettingsField[] = [];
  for (const [key, entry] of Object.entries(properties)) {
    const property = asRecord(entry);
    if (property === null) continue;
    const kind = fieldKind(property);
    if (kind === null) continue;
    const title = property['title'];
    const description = property['description'];
    fields.push({
      key,
      label: typeof title === 'string' && title !== '' ? title : key,
      hint: typeof description === 'string' && description !== '' ? description : null,
      kind,
      required: required.includes(key),
      defaultValue: property['default'],
    });
  }
  return fields;
}

export function settingsDraft(fields: SettingsField[], settings?: JsonObject): FormDraft {
  const draft: FormDraft = {};
  for (const field of fields) {
    const stored = settings?.[field.key];
    const value = stored === undefined || stored === null ? field.defaultValue : stored;
    if (field.kind === 'boolean') {
      draft[field.key] = value === true;
    } else if (field.kind === 'lines') {
      draft[field.key] = Array.isArray(value) ? value.map((item) => String(item)).join('\n') : '';
    } else {
      draft[field.key] = value === undefined || value === null ? '' : String(value);
    }
  }
  return draft;
}

export function settingsValue(
  fields: SettingsField[],
  draft: FormDraft,
): { value: JsonObject; issues: FieldIssue[] } {
  const value: JsonObject = {};
  const issues: FieldIssue[] = [];
  for (const field of fields) {
    const raw = draft[field.key];
    if (field.kind === 'boolean') {
      value[field.key] = raw === true;
      continue;
    }
    const text = typeof raw === 'string' ? raw : '';
    if (field.kind === 'lines') {
      const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');
      if (lines.length === 0) {
        if (field.required) issues.push({ key: field.key, message: '必須項目です。' });
        continue;
      }
      value[field.key] = lines;
      continue;
    }
    const trimmed = text.trim();
    if (trimmed === '') {
      if (field.required) issues.push({ key: field.key, message: '必須項目です。' });
      continue;
    }
    if (field.kind === 'integer') {
      if (!/^-?\d+$/.test(trimmed)) {
        issues.push({ key: field.key, message: '整数で入力してください。' });
        continue;
      }
      value[field.key] = Number(trimmed);
      continue;
    }
    if (field.kind === 'number') {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        issues.push({ key: field.key, message: '数値で入力してください。' });
        continue;
      }
      value[field.key] = parsed;
      continue;
    }
    value[field.key] = trimmed;
  }
  return { value, issues };
}

/**
 * 資格情報の本文。新規は null（欄が無い種類）か全欄そろった値、編集は undefined（送らない＝今の値を残す）
 * か入力のあった欄だけ。値そのものは呼び出し側も保存しない（送ったら捨てる）。
 */
export function credentialValue(
  fields: KnowledgeCredentialField[],
  draft: Record<string, string>,
  editing: boolean,
): { value: Record<string, string> | null | undefined; issues: FieldIssue[] } {
  if (fields.length === 0) return { value: editing ? undefined : null, issues: [] };
  const issues: FieldIssue[] = [];
  const value: Record<string, string> = {};
  for (const field of fields) {
    const entry = (draft[field.key] ?? '').trim();
    if (entry === '') {
      if (!editing) issues.push({ key: field.key, message: '必須項目です。' });
      continue;
    }
    value[field.key] = entry;
  }
  if (issues.length > 0 || Object.keys(value).length === 0) return { value: undefined, issues };
  return { value, issues };
}
